import { toast } from "@/components/common/Toast"
import { PAID_GROUPS } from "@/constants/paidGroups"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { amplitude } from "@/services/analytics/amplitude"
import { User } from "@/types/user"
import { useAuthenticator } from "@aws-amplify/ui-react"
import * as Sentry from "@sentry/react"
import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useState } from "react"
import { performSignOut } from "../services/api/performSignOut"
import { useGetUserData } from "../services/api/userService"
import { setUserContext } from "../utils/metadata"
import { useTranslation } from "./useTranslation/useTranslation"

const useUser = () => {
  const { translate } = useTranslation()
  const queryClient = useQueryClient()
  const { authStatus } = useAuthenticator((context) => [context.authStatus])
  const [user, setUser] = useState<User | null>()
  const userDataErrorToastId = "user-data-error"
  // Kill-switch: the whole granular-permission system is only enforced when
  // TEAM_MANAGEMENT is on for the org. Mirrors the backend's require_permission
  // behavior (backend/turnus_ai/api/auth.py:195) — flag off means every
  // authenticated user passes, so every FE gate must also degrade to "allow".
  const teamManagementEnabled = useFeatureFlag("TEAM_MANAGEMENT")

  const {
    data,
    isLoading: isUserDataLoading,
    error,
  } = useGetUserData(authStatus === "authenticated")
  // hasPermission needs to distinguish "still fetching /users/me" from "logged
  // out": both leave `user` falsy but a logged-out user must NOT wildcard-pass
  // gates. Track loading only while auth says we should have data coming.
  const isLoading = authStatus === "authenticated" && isUserDataLoading

  useEffect(() => {
    if (!isUserDataLoading && data) {
      const {
        username,
        email,
        organization,
        id,
        groups,
        role,
        isAdmin,
        permissions,
      } = data
      setUser({
        username,
        email,
        organization,
        id,
        groups,
        role,
        isAdmin,
        permissions,
      })
      try {
        setUserContext({
          user_id: id,
          organization_id: organization,
          email,
          groups,
        })
        Sentry.setUser({ id, email, username })
        Sentry.setTag("organization_id", organization)
        const identifyObj = new amplitude.Identify()
        identifyObj.set("organization_id", organization)
        identifyObj.set("groups", groups)
        amplitude.identify(identifyObj)
      } catch {
        // never block user flow
      }
    }
    if (error) {
      toast.error(translate("USER_DATA_ERROR"), {
        id: userDataErrorToastId,
      })
    }
  }, [data, isUserDataLoading, error, translate])

  const handleLogout = useCallback(async () => {
    const outcome = await performSignOut(queryClient, () => setUser(null))
    if (outcome === "failed") toast.error(translate("USER_ERROR_SIGNOUT"))
  }, [translate, queryClient])

  // The paid groups beat both trial groups (backend limits.py
  // resolve_package_group): a token that still lists a trial group next to one
  // is a paying user.
  const isFreeTrial =
    !PAID_GROUPS.some((group) => user?.groups.includes(group)) &&
    (user?.groups.includes("FreeTrial") ||
      user?.groups.includes("ExtendedFreeTrial"))

  // The single source of truth for "can this user do X" — every FE gate
  // (RequirePermission route wrapper, sidebar filter, PermissionGate button
  // wrapper, useGuardedMutation hook net) ultimately calls this. Order matters:
  //   1. Flag off          → allow (BE's require_permission is also a no-op)
  //   2. Not authenticated → deny (post-logout / pre-login; fail closed so a
  //                          stale query cache can't re-open gated UI in the
  //                          frame before <Navigate> tears things down)
  //   3. Loading           → allow, so the app doesn't flash-hide items
  //                          before /users/me lands
  //   4. No data (errored) → deny; a failed /users/me leaves data undefined
  //                          with isLoading=false, and returning allow there
  //                          would fail open on a permanently broken session
  //   5. Admin             → allow (wildcard, matches BE OrgRole.ADMIN check)
  //   6. Otherwise         → check the granted keys from /users/me
  //
  // Reads `data` from React Query directly rather than the mirrored `user`
  // state above — setUser runs in a useEffect, so `user` lags `data` by one
  // render on the frame /users/me settles. That gap used to redirect admins
  // home (isLoading=false, user still undefined, admin bypass unreachable).
  const hasPermission = useCallback(
    (permissionKey: string): boolean => {
      if (!teamManagementEnabled) return true
      if (authStatus === "unauthenticated") return false
      if (isLoading) return true
      if (!data) return false
      if (data.isAdmin) return true
      return data.permissions?.includes(permissionKey) ?? false
    },
    [teamManagementEnabled, authStatus, isLoading, data],
  )

  return {
    user,
    isFreeTrial: isFreeTrial || undefined,
    isAdmin: user?.isAdmin ?? false,
    hasPermission,
    handleLogout,
  }
}

export default useUser
